import AntIcon from "@/components/icons/AntIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import { getApiBaseUrl } from "@/utils/utils";
import { Button, Flex, Image, Popconfirm, Upload } from "antd";
import { RcFile } from "antd/lib/upload";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import UserOutlined from "../../../assets/images/UserOutlined.svg";
import { useDeleteAgentProfilePicture, useUpdateAgentProfilePicture } from "../../../common/api/queries";
import { FileUploadAccept } from "../../../common/constants";
import { useAuthToken, useCurrentAccount } from "../../auth/hooks";
import type { Agent, AgentProfile, AgentProfilePicture } from "../types";

interface Props {
  agent: AgentProfile | Agent;
  width?: number;
  height?: number;
  preview?: boolean;
  showActions?: boolean;
  className?: string;
  updateProfilePictureCallback?: (profilePicture?: AgentProfilePicture) => void;
}

export const AgentProfilePictureEditor = ({
  agent,
  width,
  height,
  preview = true,
  showActions,
  className,
  updateProfilePictureCallback,
}: Props) => {
  const { t } = useTranslation();

  const token = useAuthToken();
  const userAccount = useCurrentAccount();

  const updateAgentProfilePicture = useUpdateAgentProfilePicture();
  const deleteAgentProfilePicture = useDeleteAgentProfilePicture();

  const handleProfilePictureUpload = async (file: RcFile) => {
    const formData = new FormData();
    formData.append("file", file);

    const profilePicture = await updateAgentProfilePicture.mutateAsync({ id: agent.id, object: formData });
    updateProfilePictureCallback?.(profilePicture);

    return false;
  };

  return agent ? (
    <div className={classNames("center-align", className)}>
      <Image
        key={agent.profilePicture?.id}
        width={width}
        height={height}
        preview={preview}
        src={
          agent.profilePicture
            ? `${getApiBaseUrl()}/agents/${agent.id}/profile-picture?auth=${token}&accountId=${userAccount?.id}`
            : ""
        }
        fallback={UserOutlined}
      />

      {showActions && (
        <Flex style={{ width }} justify="space-between" className="margin-top-tiny">
          <Upload accept={FileUploadAccept.IMAGE} showUploadList={false} beforeUpload={handleProfilePictureUpload}>
            <Button variant="outlined" icon={<AntIcon type="edit" />} size="small" color="blue">
              {t("common.change")}
            </Button>
          </Upload>

          <Popconfirm
            title={t("agent.titles.deleteProfilePictureConfirm")}
            icon={<PopconfirmDeleteIcon />}
            okText={t("common.yes")}
            cancelText={t("common.no")}
            okType="danger"
            onConfirm={() => {
              deleteAgentProfilePicture.mutate(agent.id);
              updateProfilePictureCallback?.();
            }}
            disabled={!agent.profilePicture}
          >
            <Button
              variant="outlined"
              icon={<AntIcon type="delete" />}
              color="red"
              size="small"
              disabled={!agent.profilePicture}
            >
              {t("common.remove")}
            </Button>
          </Popconfirm>
        </Flex>
      )}
    </div>
  ) : null;
};
